// Club settings (X103 G3): the page at /admin/clubs/{clubId}/settings, where the club's own details,
// logo, who can see it, joining and deletion live (the dashboard's Club setup tiles used to hold them).
// Pill tabs as the Bookings and Memberships pages (TlPillTabs), the tab in ?tab= via router.replace.
import {computed, unref} from 'vue';
import {pickTab} from '~/utils/pillTabs';
import {CLUB_TIER, clubVisibilityTier} from '~/utils/clubPlayers';

export const SETTINGS_TABS = Object.freeze([
  {value: 'details', title: 'Details', icon: 'mdi-card-text-outline'},
  {value: 'logo', title: 'Logo', icon: 'mdi-shield-account-outline'},
  // X113 (R49 L3): no phone shortTitle ("Visibility"): the Memberships card's link and the Players banner say "Club
  // settings › Who can see the club", so the pill says the same at every width and the row scrolls when it must.
  {value: 'visibility', title: 'Who can see the club', icon: 'mdi-lock-outline'},
  // X155 (Grant 2026-10-27): "Invite link", not "Joining": the tab is the club's own invite link and its settings (the ?tab=joining value stays).
  {value: 'joining', title: 'Invite link', icon: 'mdi-account-multiple-plus'},
]);

export function pickSettingsTab(query) {
  return pickTab(SETTINGS_TABS, query, 'details');
}

/** The route to a settings tab; the Details tab carries no ?tab=. */
export function settingsRoute(clubId, tab = 'details') {
  return {name: 'ClubSettings', params: {clubId}, ...(tab && tab !== 'details' ? {query: {tab}} : {})};
}

/**
 * X108: the chip by the page title (as the Bookings page's "Bookings on"): who can see the club right now.
 * null until the club is loaded. The third tier reads as club players only here; the Who can see the club
 * tab says the rest.
 */
export function settingsStateChip(club) {
  if (!club) return null;
  const tier = clubVisibilityTier(club.visibility, club.playersVisibleTo);
  return tier === CLUB_TIER.public ? {label: 'Public', ok: true} : {label: 'Club players only', ok: false};
}

/**
 * X110 (R46 L4): what the Club settings forms compare to know whether anything changed. The form fills three
 * defaults on an empty club when it mounts (ClubForm: hasFixedAddress true, visibility 1, listedInSearch true), so
 * both sides are read with them filled and a club that only gained its defaults is not "changed".
 */
export function clubSnapshot(club) {
  if (!club) return null;
  return JSON.stringify({
    ...club,
    hasFixedAddress: club.hasFixedAddress === undefined ? true : club.hasFixedAddress,
    visibility: club.visibility ?? 1,
    listedInSearch: club.listedInSearch ?? true,
  });
}

/** True when the draft differs from the club it was copied from (deep, with the form's defaults filled). */
export function clubDraftChanged(club, draft) {
  return clubSnapshot(club) !== clubSnapshot(draft);
}

/** The page's reactive version: `draftChanged` follows the club and the draft. */
export function useClubDraft(club, draft) {
  const draftChanged = computed(() => clubDraftChanged(unref(club), unref(draft)));
  return {draftChanged};
}
